/**
 * ─────────────────────────────────────────────────────────────────────────────
 * After Summit — Design Token Registry
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This is the SINGLE SOURCE OF TRUTH for all theme values.
 * CSS variables are derived from this file.
 * Tailwind config extends from this file.
 * Components reference CSS variables — never raw hex values.
 *
 * Brand palette:
 *   Primary (brand purple):  #8B5CF6  (violet500)
 *   Primary hover:           #7C3AED  (violet600)
 *   Primary light (dark bg): #A78BFA  (violet400)
 *   Dark background:         #0F172A  (slate900)
 *   Dark surface:            #1E293B  (slate800)
 *
 * Token naming convention:
 *   --ac-<category>-<role>       (ac = After Summit token namespace)
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Raw Palette (static — never change per theme) ────────────────────────────
export const palette = {
  // Slate scale
  slate50:  '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1E293B',
  slate900: '#0F172A',
  slate950: '#020617',

  // Violet scale
  violet300: '#C4B5FD',
  violet400: '#A78BFA',
  violet500: '#8B5CF6',
  violet600: '#7C3AED',
  violet700: '#6D28D9',

  // Amber scale
  amber300: '#FCD34D',
  amber400: '#FBBF24',
  amber500: '#F59E0B',
  amber600: '#D97706',

  // Emerald scale
  emerald400: '#34D399',
  emerald500: '#10B981',
  emerald600: '#059669',

  // Red scale
  red400: '#F87171',
  red500: '#EF4444',
  red600: '#DC2626',

  // Sky scale
  sky400:  '#38BDF8',
  sky500:  '#0EA5E9',

  // White / Black
  white: '#FFFFFF',
  black: '#000000',
}

// ── Semantic Tokens — Light Theme ────────────────────────────────────────────
export const lightTokens = {
  // Backgrounds
  'bg-base':     palette.slate50,      // #F8FAFC — page background
  'bg-surface':  palette.white,        // #FFFFFF — card / panel surface
  'bg-subtle':   palette.slate100,     // #F1F5F9 — subtle bg (inputs, tags)
  'bg-overlay':  'rgba(15,23,42,0.5)', // modal backdrop

  // Brand colors
  'primary':       palette.slate800,  // #2D3748 — mapped from spec
  'primary-hover': palette.slate700,
  'primary-fg':    palette.white,

  'secondary':       palette.violet500, // #8B5CF6 — brand primary purple
  'secondary-hover': palette.violet600, // #7C3AED
  'secondary-fg':    palette.white,

  'accent':       palette.amber500,   // #F59E0B
  'accent-hover': palette.amber600,
  'accent-fg':    palette.slate900,

  // Text
  'text-base':      palette.slate800,  // #1E293B
  'text-muted':     palette.slate500,
  'text-subtle':    palette.slate400,
  'text-inverse':   palette.white,
  'text-on-primary': palette.white,

  // Border
  'border-base':   palette.slate200,  // #E2E8F0
  'border-strong': palette.slate300,
  'border-focus':  palette.violet600,

  // Status
  'success':    palette.emerald500,   // #10B981
  'success-bg': 'rgba(16,185,129,0.10)',
  'success-fg': palette.emerald600,

  'danger':    palette.red500,        // #EF4444
  'danger-bg': 'rgba(239,68,68,0.10)',
  'danger-fg': palette.red600,

  'warning':    palette.amber500,
  'warning-bg': 'rgba(245,158,11,0.10)',
  'warning-fg': palette.amber600,

  'info':    palette.sky500,
  'info-bg': 'rgba(14,165,233,0.10)',
  'info-fg': palette.sky400,

  // Glassmorphism
  'glass-bg':     'rgba(255,255,255,0.60)',
  'glass-border': 'rgba(255,255,255,0.20)',
  'glass-shadow': '0 8px 32px rgba(15,23,42,0.12)',

  // Shadows
  'shadow-sm': '0 1px 2px rgba(15,23,42,0.06)',
  'shadow-md': '0 4px 12px rgba(15,23,42,0.08)',
  'shadow-lg': '0 10px 30px rgba(15,23,42,0.10)',
  'shadow-xl': '0 20px 50px rgba(15,23,42,0.12)',
}

// ── Semantic Tokens — Dark Theme ─────────────────────────────────────────────
export const darkTokens = {
  // Backgrounds
  'bg-base':     palette.slate900,     // #0F172A
  'bg-surface':  palette.slate800,     // #1E293B
  'bg-subtle':   'rgba(51,65,85,0.50)',
  'bg-overlay':  'rgba(2,6,23,0.70)',

  // Brand colors
  'primary':       palette.slate300,  // #CBD5E1
  'primary-hover': palette.slate200,
  'primary-fg':    palette.slate900,

  'secondary':       palette.violet400, // #A78BFA
  'secondary-hover': palette.violet300,
  'secondary-fg':    palette.slate900,

  'accent':       palette.amber400,   // #FBBF24
  'accent-hover': palette.amber300,
  'accent-fg':    palette.slate900,

  // Text
  'text-base':      palette.slate50,   // #F8FAFC
  'text-muted':     palette.slate400,
  'text-subtle':    palette.slate500,
  'text-inverse':   palette.slate900,
  'text-on-primary': palette.slate900,

  // Border
  'border-base':   palette.slate700,  // #334155
  'border-strong': palette.slate600,
  'border-focus':  palette.violet400,

  // Status
  'success':    palette.emerald400,
  'success-bg': 'rgba(52,211,153,0.12)',
  'success-fg': palette.emerald400,

  'danger':    palette.red400,
  'danger-bg': 'rgba(248,113,113,0.12)',
  'danger-fg': palette.red400,

  'warning':    palette.amber400,
  'warning-bg': 'rgba(251,191,36,0.12)',
  'warning-fg': palette.amber400,

  'info':    palette.sky400,
  'info-bg': 'rgba(56,189,248,0.12)',
  'info-fg': palette.sky400,

  // Glassmorphism
  'glass-bg':     'rgba(15,23,42,0.60)',
  'glass-border': 'rgba(51,65,85,0.40)',
  'glass-shadow': '0 8px 32px rgba(2,6,23,0.40)',

  // Shadows
  'shadow-sm': '0 1px 2px rgba(2,6,23,0.20)',
  'shadow-md': '0 4px 12px rgba(2,6,23,0.30)',
  'shadow-lg': '0 10px 30px rgba(2,6,23,0.40)',
  'shadow-xl': '0 20px 50px rgba(2,6,23,0.50)',
}

// ── Static Tokens (same in both themes) ──────────────────────────────────────
export const staticTokens = {
  // Typography
  'font-display': "'Syne', 'Georgia', serif",
  'font-body':    "'DM Sans', system-ui, sans-serif",
  'font-mono':    "'JetBrains Mono', ui-monospace, monospace",

  // Font sizes
  'text-xs':   '0.75rem',
  'text-sm':   '0.875rem',
  'text-base': '1rem',
  'text-lg':   '1.125rem',
  'text-xl':   '1.25rem',
  'text-2xl':  '1.5rem',
  'text-3xl':  '1.875rem',
  'text-4xl':  '2.25rem',
  'text-5xl':  '3rem',

  // Spacing
  'space-1':  '0.25rem',
  'space-2':  '0.5rem',
  'space-3':  '0.75rem',
  'space-4':  '1rem',
  'space-5':  '1.25rem',
  'space-6':  '1.5rem',
  'space-8':  '2rem',
  'space-10': '2.5rem',
  'space-12': '3rem',
  'space-16': '4rem',

  // Border radius
  'radius-xs':   '2px',
  'radius-sm':   '4px',
  'radius-md':   '8px',
  'radius-lg':   '12px',
  'radius-xl':   '16px',
  'radius-2xl':  '24px',
  'radius-full': '9999px',

  // Layout
  'sidebar-w':           '260px',
  'sidebar-w-collapsed': '72px',
  'header-h':            '64px',
  'content-max-w':       '1280px',

  // Transitions
  'duration-fast':   '150ms',
  'duration-base':   '250ms',
  'duration-slow':   '350ms',
  'easing-default':  'cubic-bezier(0.4, 0, 0.2, 1)',
  'easing-spring':   'cubic-bezier(0.34, 1.56, 0.64, 1)',
  'easing-out':      'cubic-bezier(0.0, 0, 0.2, 1)',

  // Z-index
  'z-base':    '0',
  'z-raised':  '10',
  'z-sticky':  '100',
  'z-overlay': '200',
  'z-modal':   '300',
  'z-toast':   '400',
}

// ── Theme modes ───────────────────────────────────────────────────────────────
export const THEMES = {
  LIGHT:  'light',
  DARK:   'dark',
  SYSTEM: 'system',
}

export const DEFAULT_THEME = THEMES.SYSTEM
